'use client';

import { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import HeaderMenuImage from '@/components/sections/builder/elements/header/header/header-menu-image';
import {
  HeaderElementDataType,
  HeaderMenuElementDataType,
} from '@/components/sections/builder/elements/header/header/type';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { IMenuItem } from '@/types/navigation';
import { ChevronDownIcon } from 'lucide-react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { HeaderSubMenuType } from '@/components/sections/builder/settings/header-sub-menu-setting';

export default function HeaderMenuContent({
  menu,
  isMegaMenu,
  parentData,
  preview,
  openMenuId,
  setOpenMenuId,
  openSubMenuId,
  setOpenSubMenuId,
}: {
  menu: IMenuItem;
  isMegaMenu: boolean;
  parentData?: HeaderElementDataType;
  preview?: boolean;
  openMenuId?: string;
  setOpenMenuId?: Dispatch<SetStateAction<string>>;
  openSubMenuId?: string;
  setOpenSubMenuId?: Dispatch<SetStateAction<string>>;
}) {
  const query = useSearchParams();
  const themeId: string = query.get('theme_id') || '';
  const token = query.get('token') || '';

  const [isSticky, setIsSticky] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const flattenChildrenToSubMenus = (
    children: IMenuItem[] | undefined,
    subMenus: HeaderSubMenuType[] = [],
    groupIndex: number = 0
  ): HeaderSubMenuType[] => {
    if (!children) return subMenus;
    for (const child of children) {
      subMenus.push({
        id: child.id,
        label: child.title,
        link: child.url_path,
        groupIndex,
        groupLabel: undefined,
      });

      if (child.children?.length) {
        flattenChildrenToSubMenus(child.children, subMenus, groupIndex);
      }
    }
    return subMenus;
  };

  const transformMenuToHeaderData = (
    menu: IMenuItem,
    isMegaMenu = false
  ): HeaderMenuElementDataType => {
    const hasChildren = menu.children && menu.children.length > 0;

    return {
      name: menu.title,
      label: menu.title,
      link: menu.url_path || undefined,
      subMenuType: !hasChildren ? 'NONE' : isMegaMenu ? 'MEGA_MENU' : 'DROPDOWN',
      dropdownWidth: 'AUTO',
      megaMenuWidth: 'FULL_WIDTH',
      subMenus: hasChildren ? flattenChildrenToSubMenus(menu.children) : [],
    };
  };

  const data = transformMenuToHeaderData(menu, isMegaMenu);

  return (
    <>
      {/* Mobile components */}
      <Accordion type='single' collapsible className='block w-full lg:hidden'>
        <AccordionItem value='item-1' className='w-full'>
          <AccordionTrigger
            className={cn(
              'w-full flex-row-reverse justify-between text-base  font-normal [&>svg]:h-5 [&>svg]:w-5',
              data.subMenuType === 'NONE' && '[&>svg]:opacity-0'
            )}
          >
            {data.subMenuType === 'NONE' ? (
              <Link
                href={
                  data.link?.includes('http')
                    ? data.link
                    : `/${data.link}${
                        token
                          ? `?theme_id=${themeId}&token=${token}`
                          : preview
                          ? `?theme_id=${themeId}&isPreview=true`
                          : ''
                      }`
                }
              >
                {data.label}
              </Link>
            ) : (
              data.label
            )}
          </AccordionTrigger>
          <AccordionContent className='text-left'>
            {data.subMenuType === 'DROPDOWN' && (
              <ul className='py-2 text-base text-gray-700 dark:text-gray-400'>
                {menu.children?.map((subMenu) => (
                  <li key={subMenu.id}>
                    <Link
                      href={
                        subMenu.url_path.includes('http')
                          ? subMenu.url_path
                          : `/${subMenu.url_path}${
                              token
                                ? `?theme_id=${themeId}&token=${token}`
                                : preview
                                ? `?theme_id=${themeId}&isPreview=true`
                                : ''
                            }`
                      }
                      className='menu-item-with-hover-border relative block rounded-sm px-4 py-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-left after:scale-x-0 after:transition-transform after:duration-300 after:content-[""] hover:after:scale-x-100'
                    >
                      {subMenu.title}
                    </Link>
                    {subMenu.children?.length > 0 && (
                      <button
                        onClick={() =>
                          setOpenSubMenuId?.(openSubMenuId === subMenu.id ? '' : subMenu.id)
                        }
                      ></button>
                    )}
                    {openSubMenuId === subMenu.id && subMenu.children?.length > 0 && (
                      <ul className='pl-4'>
                        {subMenu.children.map((child) => (
                          <li key={child.id}>
                            <Link
                              href={
                                child.url_path.includes('http')
                                  ? child.url_path
                                  : `/${child.url_path}${
                                      token
                                        ? `?theme_id=${themeId}&token=${token}`
                                        : preview
                                        ? `?theme_id=${themeId}&isPreview=true`
                                        : ''
                                    }`
                              }
                              className='menu-item-with-hover-border relative block rounded-sm px-4 py-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-left after:scale-x-0 after:transition-transform after:duration-300 after:content-[""] hover:after:scale-x-100'
                            >
                              {child.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {data.subMenuType === 'MEGA_MENU' && (
              <div className='flex w-full flex-col justify-between gap-4 px-3 py-2'>
                {menu.children?.map((menuItem) => (
                  <div key={menuItem.id}>
                    {menuItem.title && (
                      <Link
                        href={
                          menuItem.url_path.includes('http')
                            ? menuItem.url_path
                            : `/${menuItem.url_path}${
                                token
                                  ? `?theme_id=${themeId}&token=${token}`
                                  : preview
                                  ? `?theme_id=${themeId}&isPreview=true`
                                  : ''
                              }`
                        }
                        className={cn(
                          'mb-1 text-base',
                          menuItem.children.length
                            ? 'font-semibold text-black'
                            : 'menu-item-with-hover-border relative rounded-lg p-2 text-base font-normal text-[#52535B] after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-left after:scale-x-0 after:transition-transform after:duration-300 after:content-[""] hover:after:scale-x-100'
                        )}
                      >
                        {menuItem.title}
                      </Link>
                    )}
                    <ul>
                      {menuItem.children?.map((subMenu) => (
                        <li key={subMenu.id}>
                          <Link
                            href={
                              subMenu.url_path?.includes('http')
                                ? subMenu.url_path
                                : `/${subMenu.url_path}${
                                    token
                                      ? `?theme_id=${themeId}&token=${token}`
                                      : preview
                                      ? `?theme_id=${themeId}&isPreview=true`
                                      : ''
                                  }`
                            }
                            className='menu-item-with-hover-border relative flex gap-x-2 rounded-lg p-3 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-left after:scale-x-0 after:transition-transform after:duration-300 after:content-[""] hover:after:scale-x-100'
                          >
                            <div>
                              <div>{subMenu.title}</div>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <div className='grid grid-cols-1 gap-2'>
                  {menu.images?.slice(0, 3).map((image, index) => (
                    <Link
                      key={index}
                      href={
                        image.url_path?.includes('http')
                          ? image.url_path
                          : `/${image.url_path}${
                              token
                                ? `?theme_id=${themeId}&token=${token}`
                                : preview
                                ? `?theme_id=${themeId}&isPreview=true`
                                : ''
                            }`
                      }
                      passHref={image.url_path?.includes('http')}
                    >
                      <HeaderMenuImage
                        title={image.title}
                        src={image.image_url}
                        parentData={parentData}
                      />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Desktop components */}
      <div
        ref={dropdownRef}
        className={cn(
          'hidden md:inline-block',
          isSticky && 'z-50 w-full transition-all duration-300'
        )}
      >
        <div
          className={cn(
            'menu-item-with-hover-border relative hidden flex-shrink-0 cursor-pointer items-center gap-x-2 text-base after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-left after:scale-x-0 after:transition-transform after:duration-300 after:content-[""] hover:after:scale-x-100 lg:flex',
            menu.is_highlight && 'rounded bg-red-500 px-2 py-1 text-white'
          )}
          onMouseEnter={() => {
            if (data.subMenuType === 'NONE') {
              return;
            } else {
              if (openMenuId === menu.id) {
                setOpenMenuId?.('');
              } else {
                setOpenMenuId?.(menu.id);
              }
            }
          }}
        >
          {data.subMenuType === 'NONE' ? (
            <Link
              className='flex-shrink-0'
              href={
                (data.link ?? '').includes('http')
                  ? data.link ?? ''
                  : `/${data.link ?? ''}${
                      token
                        ? `?theme_id=${themeId}&token=${token}`
                        : preview
                        ? `?theme_id=${themeId}&isPreview=true`
                        : ''
                    }`
              }
            >
              {data.label}
            </Link>
          ) : (
            <span>{data.label}</span>
          )}
          {data.subMenuType !== 'NONE' && <ChevronDownIcon className='h-4 w-4' />}
        </div>
        {openMenuId === menu.id && data.subMenuType === 'DROPDOWN' && (
          <div
            className={cn(
              'absolute z-50 mt-2 w-full min-w-[200px] rounded-md border bg-white shadow-lg',
              'theme_dropdown_popup_border theme_font theme_dropdown_popup_shadow p-2',
              parentData?.colorSchemeMenuHeader?.background
                ? 'scheme-color-menu-header'
                : 'bg-white',
              data.dropdownWidth === 'AUTO' && 'w-auto',
              data.dropdownWidth === 'MEDIUM' && 'w-80',
              data.dropdownWidth === 'LARGE' && 'w-96'
            )}
            onMouseLeave={() => {
              setOpenMenuId?.('');
              setOpenSubMenuId?.('');
            }}
          >
            <ul className='w-full py-2 text-base'>
              {menu.children?.map((subMenu) => (
                <li key={subMenu.id} className='relative'>
                  <div className='menu-item-with-hover-border relative flex cursor-pointer items-center justify-between rounded px-4 py-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-left after:scale-x-0 after:transition-transform after:duration-300 after:content-[""] hover:after:scale-x-100'>
                    <Link
                      href={
                        subMenu.url_path?.includes('http')
                          ? subMenu.url_path
                          : `/${subMenu.url_path}${
                              token
                                ? `?theme_id=${themeId}&token=${token}`
                                : preview
                                ? `?theme_id=${themeId}&isPreview=true`
                                : ''
                            }`
                      }
                      className='text-base'
                    >
                      {subMenu.title}
                    </Link>
                    {subMenu.children.length > 0 && (
                      <ChevronDownIcon
                        className={`w-4 transition-transform ${
                          openSubMenuId === subMenu.id ? 'rotate-180' : ''
                        }`}
                        onClick={() => {
                          if (openSubMenuId === subMenu.id) {
                            setOpenSubMenuId?.('');
                          } else {
                            setOpenSubMenuId?.(subMenu.id);
                          }
                        }}
                      />
                    )}
                  </div>
                  {openSubMenuId === subMenu.id && subMenu.children.length > 0 && (
                    <ul className='pl-1.5'>
                      {subMenu.children.map((child) => (
                        <li key={child.id}>
                          <Link
                            href={
                              child.url_path.includes('http')
                                ? child.url_path
                                : `/${child.url_path}${
                                    token
                                      ? `?theme_id=${themeId}&token=${token}`
                                      : preview
                                      ? `?theme_id=${themeId}&isPreview=true`
                                      : ''
                                  }`
                            }
                            className='menu-item-with-hover-border relative block rounded-sm px-4 py-2 text-base after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-left after:scale-x-0 after:transition-transform after:duration-300 after:content-[""] hover:after:scale-x-100'
                          >
                            {child.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {openMenuId === menu.id && data.subMenuType === 'MEGA_MENU' && (
          <>
            <div
              onClick={() => setOpenMenuId?.('')}
              className={cn(
                'absolute left-0 top-full z-40 h-screen w-full bg-black/20 backdrop-blur-sm',
                'transition-opacity duration-300 ease-in-out',
                openMenuId === menu.id ? 'opacity-100' : 'pointer-events-none opacity-0'
              )}
            />
            <div
              onMouseLeave={() => {
                setTimeout(() => {
                  setOpenMenuId?.('');
                }, 200);
              }}
              data-test-id='header-menu-content-mega-menu'
              className={cn(
                'bordershadow-lg absolute inset-x-0 top-full z-50 min-h-[400px] overflow-y-auto',
                'theme_font flex justify-center border-t border-gray-200 py-10',
                'transition-all duration-300 ease-in-out',
                openMenuId === menu.id
                  ? 'translate-y-0 opacity-100'
                  : 'pointer-events-none -translate-y-4 opacity-0',
                parentData?.colorSchemeMenuHeader?.background
                  ? 'scheme-color-menu-header'
                  : 'bg-white'
              )}
            ></div>
          </>
        )}
      </div>
    </>
  );
}
